import { useState, useEffect, useCallback, lazy, Suspense } from 'react';
import { Routes, Route, Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useBranding } from '@/contexts/BrandingContext';
import axios from 'axios';
import {
    FileText, Truck as TruckIcon, MessageSquare, Tag,
    ShoppingCart, LogOut, Menu, X, KeyRound, BarChart3, HelpCircle, Loader2, ChevronRight
} from 'lucide-react';
import { toast } from 'sonner';

import HelpDialog from '@/components/HelpDialog';
const ClientWelcomePage = lazy(() => import('./ClientWelcomePage'));
const ClientPriceListsPage = lazy(() => import('./ClientPriceListsPage'));
const ClientInvoicesPage = lazy(() => import('./ClientInvoicesPage'));
const ClientDDTPage = lazy(() => import('./ClientDDTPage'));
const ClientCommunicationsPage = lazy(() => import('./ClientCommunicationsPage'));
const ClientOrdersPage = lazy(() => import('./ClientOrdersPage'));
const ClientStatisticsPage = lazy(() => import('./ClientStatisticsPage'));

const API = `${typeof window !== "undefined" ? window.location.origin : process.env.REACT_APP_BACKEND_URL || ""}/api`;

// Palette fissa, uguale per tutti i tenant. Il colore brand del tenant è usato solo per il logo.
const CLIENT_SHELL_STYLE = {
    fontFamily: "'Plus Jakarta Sans', sans-serif",
    '--brand-color': '#173d2b',
    '--brand-color-secondary': '#FFCC00',
};

const navItems = [
    { path: '/client/orders', icon: ShoppingCart, label: 'Ordini', key: 'orders', chip: 'bg-blue-50 border-blue-100', chipIcon: 'text-blue-600' },
    { path: '/client/statistics', icon: BarChart3, label: 'Statistiche', key: 'statistics', chip: 'bg-purple-50 border-purple-100', chipIcon: 'text-purple-600' },
    { path: '/client/price-lists', icon: Tag, label: 'Listini Prezzi', key: 'price_list', chip: 'bg-emerald-50 border-emerald-100', chipIcon: 'text-emerald-600' },
    { path: '/client/invoices', icon: FileText, label: 'Fatture', key: 'invoice', chip: 'bg-indigo-50 border-indigo-100', chipIcon: 'text-indigo-600' },
    { path: '/client/ddt', icon: TruckIcon, label: 'DDT', key: 'ddt', chip: 'bg-amber-50 border-amber-100', chipIcon: 'text-amber-600' },
    { path: '/client/communications', icon: MessageSquare, label: 'Comunicazioni', key: 'communications', chip: 'bg-sky-50 border-sky-100', chipIcon: 'text-sky-600' },
];

function BrandMark({ branding, size = 'md' }) {
    const box = size === 'md' ? 'h-9 w-9' : 'h-10 w-10';
    return branding.logo_url ? (
        <img src={branding.logo_url} alt={branding.name || 'Logo'} className={`${box} object-contain rounded-lg`} data-testid="client-brand-logo" />
    ) : (
        <div className={`${box} rounded-lg bg-[#173d2b] flex items-center justify-center shrink-0`} data-testid="client-brand-logo-fallback">
            <TruckIcon className="h-5 w-5 text-white" />
        </div>
    );
}

export default function ClientDashboard() {
    const { user, logout } = useAuth();
    const branding = useBranding();
    const location = useLocation();
    const navigate = useNavigate();
    const [sidebarOpen, setSidebarOpen] = useState(false);
    const [helpOpen, setHelpOpen] = useState(false);
    const [notifications, setNotifications] = useState({});

    const fetchNotifications = useCallback(async () => {
        try {
            const res = await axios.get(`${API}/client/notifications`);
            setNotifications(res.data);
        } catch (err) {
            console.error('Error fetching notifications');
        }
    }, []);

    useEffect(() => {
        fetchNotifications();
    }, [fetchNotifications]);

    // Mark as read when navigating to a section
    useEffect(() => {
        const markRead = async (docType) => {
            if (notifications[docType]) {
                try {
                    await axios.post(`${API}/client/notifications/read/${docType}`);
                    setNotifications(prev => ({ ...prev, [docType]: false }));
                } catch (err) { console.error('Errore marcatura notifica letta:', err); }
            }
        };
        if (location.pathname.includes('price-lists')) markRead('price_list');
        else if (location.pathname.includes('invoices')) markRead('invoice');
        else if (location.pathname.includes('ddt')) markRead('ddt');
        else if (location.pathname.includes('communications')) markRead('communications');
    }, [location.pathname, notifications]);

    const handleLogout = async () => {
        await logout();
        navigate('/login');
        toast.success('Logout effettuato');
    };

    const isActive = (path) => location.pathname === path || (path !== '/client' && location.pathname.startsWith(path));
    const hasNotification = (key) => notifications[key] === true;

    const NotifDot = ({ itemKey }) => {
        if (!hasNotification(itemKey)) return null;
        return itemKey === 'communications' ? (
            <span className="relative flex h-2.5 w-2.5" data-testid={`notif-${itemKey}`}>
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#B71C1C] opacity-75" />
                <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-[#B71C1C]" />
            </span>
        ) : (
            <span className="w-2.5 h-2.5 bg-[#B71C1C] rounded-full" data-testid={`notif-${itemKey}`} />
        );
    };

    return (
        <div
            className="min-h-screen relative overflow-x-hidden bg-gradient-to-b from-[#f8faf9] via-[#eef4f1] to-[#e3efe9]"
            style={CLIENT_SHELL_STYLE}
            data-testid="client-shell"
        >
            {/* Sfondo fisso: watermark simbolo OmniGlide (uguale per tutti i tenant) */}
            <div className="pointer-events-none fixed top-20 -right-12 sm:-right-16 z-0 w-[300px] sm:w-[460px] opacity-[0.06]" style={{ mixBlendMode: 'multiply' }} aria-hidden="true">
                <img src="/omniglide-icon.png" alt="" className="w-full h-auto select-none" />
            </div>

            {/* Sfondo fisso: onda decorativa (uguale per tutti i tenant) */}
            <div className="pointer-events-none fixed bottom-0 left-0 right-0 z-0 opacity-60" aria-hidden="true">
                <svg viewBox="0 0 1440 320" className="w-full h-[180px]" preserveAspectRatio="none">
                    <path fill="#d9ebe3" fillOpacity="0.9" d="M0,224L48,208C96,192,192,160,288,165.3C384,171,480,213,576,224C672,235,768,213,864,192C960,171,1056,149,1152,154.7C1248,160,1344,192,1392,208L1440,224L1440,320L0,320Z" />
                    <path fill="#cfe6f2" fillOpacity="0.6" d="M0,288L48,272C96,256,192,224,288,224C384,224,480,256,576,266.7C672,277,768,267,864,240C960,213,1056,171,1152,170.7C1248,171,1344,213,1392,234.7L1440,256L1440,320L0,320Z" />
                </svg>
            </div>

            <HelpDialog role="client" open={helpOpen} onOpenChange={setHelpOpen} />

            {/* Top bar: logo in alto a sinistra + hamburger a destra */}
            <header className="sticky top-0 z-40 bg-white/95 backdrop-blur-md border-b border-emerald-900/10 shadow-sm">
                <div className="h-16 w-full max-w-5xl mx-auto px-4 flex items-center justify-between">
                    <Link to="/client" className="flex items-center gap-2.5" data-testid="client-topbar-logo">
                        <BrandMark branding={branding} />
                        <div className="leading-tight">
                            <div className="font-extrabold text-slate-900 text-base tracking-tight" data-testid="client-brand-name">{branding.name}</div>
                            <div className="text-[11px] text-slate-400 font-medium -mt-0.5">Area cliente</div>
                        </div>
                    </Link>
                    <button
                        type="button"
                        onClick={() => setSidebarOpen(true)}
                        aria-label="Apri menu"
                        data-testid="client-menu-trigger"
                        className="h-10 w-10 rounded-xl flex items-center justify-center text-slate-700 hover:bg-slate-100 transition-colors"
                    >
                        <Menu className="h-6 w-6" />
                    </button>
                </div>
            </header>

            {/* Overlay drawer */}
            {sidebarOpen && (
                <div className="fixed inset-0 bg-slate-900/30 backdrop-blur-sm z-50" onClick={() => setSidebarOpen(false)} data-testid="client-drawer-overlay" />
            )}

            {/* Slide-in drawer */}
            <aside
                className={`fixed inset-y-0 left-0 z-50 w-[300px] sm:w-[330px] bg-white rounded-r-3xl shadow-2xl flex flex-col transition-transform duration-300 ease-out ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}`}
                data-testid="client-drawer"
            >
                {/* Header drawer */}
                <div className="p-5 flex items-center justify-between border-b border-slate-100">
                    <div className="flex items-center gap-2.5">
                        <BrandMark branding={branding} size="lg" />
                        <div className="leading-tight">
                            <div className="font-extrabold text-slate-900 text-lg tracking-tight">{branding.name}</div>
                            <div className="text-xs text-slate-400 font-medium -mt-0.5">Area cliente</div>
                        </div>
                    </div>
                    <button
                        type="button"
                        onClick={() => setSidebarOpen(false)}
                        aria-label="Chiudi menu"
                        data-testid="client-menu-close"
                        className="h-9 w-9 rounded-full flex items-center justify-center text-slate-500 hover:bg-slate-100 transition-colors"
                    >
                        <X className="h-5 w-5" />
                    </button>
                </div>

                {/* Nav */}
                <nav className="flex-1 overflow-y-auto p-3 space-y-1.5">
                    {navItems.map((item) => {
                        const active = isActive(item.path);
                        return (
                            <Link
                                key={item.path}
                                to={item.path}
                                data-testid={`client-nav-${item.key}`}
                                onClick={() => setSidebarOpen(false)}
                                className={`flex items-center justify-between gap-3 px-3 py-2.5 rounded-2xl transition-all duration-200 ${active ? 'bg-sky-50 ring-1 ring-sky-100' : 'hover:bg-slate-50'}`}
                            >
                                <span className="flex items-center gap-3">
                                    <span className={`h-10 w-10 rounded-xl border flex items-center justify-center ${item.chip}`}>
                                        <item.icon className={`h-5 w-5 ${item.chipIcon}`} />
                                    </span>
                                    <span className="font-bold text-[15px] text-slate-800">{item.label}</span>
                                </span>
                                <span className="flex items-center gap-2">
                                    <NotifDot itemKey={item.key} />
                                    <ChevronRight className="h-4 w-4 text-slate-300" />
                                </span>
                            </Link>
                        );
                    })}
                </nav>

                {/* Footer drawer: account + password + logout */}
                <div className="p-3 border-t border-slate-100 space-y-1.5">
                    <div className="flex items-center gap-3 px-3 py-2.5 rounded-2xl bg-slate-50" data-testid="client-account-block">
                        <div className="w-10 h-10 rounded-full bg-[#173d2b] flex items-center justify-center text-white font-bold shrink-0">
                            {user?.name?.charAt(0)?.toUpperCase() || 'C'}
                        </div>
                        <div className="flex-1 min-w-0">
                            <p className="text-sm font-bold text-slate-900 truncate">{user?.name}</p>
                            <p className="text-xs text-slate-400">Cliente</p>
                        </div>
                    </div>

                    <Link
                        to="/change-password"
                        onClick={() => setSidebarOpen(false)}
                        data-testid="client-change-password-btn"
                        className="flex items-center justify-between gap-3 px-3 py-2.5 rounded-2xl hover:bg-slate-50 transition-colors"
                    >
                        <span className="flex items-center gap-3">
                            <span className="h-10 w-10 rounded-xl border border-slate-200 bg-slate-100 flex items-center justify-center">
                                <KeyRound className="h-5 w-5 text-slate-600" />
                            </span>
                            <span className="font-bold text-[15px] text-slate-800">Cambia Password</span>
                        </span>
                        <ChevronRight className="h-4 w-4 text-slate-300" />
                    </Link>

                    <button
                        type="button"
                        onClick={handleLogout}
                        data-testid="client-logout-btn"
                        className="w-full flex items-center justify-between gap-3 px-3 py-2.5 rounded-2xl hover:bg-rose-50 transition-colors"
                    >
                        <span className="flex items-center gap-3">
                            <span className="h-10 w-10 rounded-xl border border-rose-100 bg-rose-50 flex items-center justify-center">
                                <LogOut className="h-5 w-5 text-rose-600" />
                            </span>
                            <span className="font-bold text-[15px] text-rose-600">Esci</span>
                        </span>
                        <ChevronRight className="h-4 w-4 text-rose-200" />
                    </button>
                </div>
            </aside>

            {/* Floating Help Button (bottom-left) */}
            <button
                type="button"
                onClick={() => setHelpOpen(true)}
                data-testid="help-button"
                aria-label="Aiuto"
                className="fixed bottom-6 left-6 z-30 h-12 w-12 rounded-full bg-white border-2 border-[#173d2b] text-[#173d2b] hover:bg-emerald-50 shadow-lg flex items-center justify-center transition-transform active:scale-95"
            >
                <HelpCircle className="h-6 w-6" />
            </button>

            {/* Main content */}
            <main className="relative z-10 w-full max-w-5xl mx-auto px-4 py-6 pb-24 min-h-[calc(100vh-4rem)]">
                <Suspense fallback={<div className="flex items-center justify-center py-20"><Loader2 className="w-6 h-6 text-[#94A3B8] animate-spin" /></div>}>
                    <Routes>
                        <Route index element={<ClientWelcomePage notifications={notifications} />} />
                        <Route path="orders" element={<ClientOrdersPage />} />
                        <Route path="statistics" element={<ClientStatisticsPage />} />
                        <Route path="price-lists" element={<ClientPriceListsPage />} />
                        <Route path="invoices" element={<ClientInvoicesPage />} />
                        <Route path="ddt" element={<ClientDDTPage />} />
                        <Route path="communications" element={<ClientCommunicationsPage />} />
                    </Routes>
                </Suspense>
            </main>
        </div>
    );
}
